import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  apiService,
  PlayerBasicInfoDTO,
  PlayerRoleDTO,
  PlayerMoneyDTO,
  NameHistoryEntry,
  HomeListItemDTO,
  HomeCoordsDTO,
  FactionInfoDTO,
  FactionHomeDTO,
  FactionMemberDTO,
  FactionClaimDTO,
  JobsInfoDTO,
  SanctionDTO,
  MarketItemDTO,
  MarketHistoryItemDTO,
  StorePointsDTO,
  StorePointTransactionDTO,
  VaultTransactionDTO,
  OnlinePlayerDTO,
} from '@/services/api.service';
import { RankManager } from '@/components/RankManager';
import styles from './PlayersPage.module.scss';

// State pour chaque section de donnees
interface PlayerData {
  basicInfo: PlayerBasicInfoDTO | null;
  role: PlayerRoleDTO | null;
  money: PlayerMoneyDTO | null;
  storePoints: StorePointsDTO | null;
  nameHistory: NameHistoryEntry[];
  personalHomes: Record<string, HomeListItemDTO[]>;
  faction: FactionInfoDTO | null;
  jobs: JobsInfoDTO | null;
  sanctions: SanctionDTO[];
  marketItems: MarketItemDTO[];
  marketHistoryCount: number;
}

interface LoadingState {
  basicInfo: boolean;
  role: boolean;
  money: boolean;
  storePoints: boolean;
  nameHistory: boolean;
  homes: boolean;
  faction: boolean;
  jobs: boolean;
  sanctions: boolean;
  market: boolean;
}

export function PlayersPage() {
  const { uuid: urlUuid } = useParams<{ uuid?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<PlayerData>({
    basicInfo: null,
    role: null,
    money: null,
    storePoints: null,
    nameHistory: [],
    personalHomes: {},
    faction: null,
    jobs: null,
    sanctions: [],
    marketItems: [],
    marketHistoryCount: 0,
  });

  const [loading, setLoading] = useState<LoadingState>({
    basicInfo: true,
    role: true,
    money: true,
    storePoints: true,
    nameHistory: true,
    homes: true,
    faction: true,
    jobs: true,
    sanctions: true,
    market: true,
  });

  // Modal pour les membres de faction
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [factionMembers, setFactionMembers] = useState<FactionMemberDTO[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Modal pour les claims de faction
  const [showClaimsModal, setShowClaimsModal] = useState(false);
  const [factionClaims, setFactionClaims] = useState<FactionClaimDTO[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(false);

  // Modal pour l'historique du market
  const [showMarketHistoryModal, setShowMarketHistoryModal] = useState(false);
  const [marketHistory, setMarketHistory] = useState<MarketHistoryItemDTO[]>([]);
  const [marketHistoryPage, setMarketHistoryPage] = useState(0);
  const [, setMarketHistoryTotal] = useState(0);
  const [marketHistoryTotalPages, setMarketHistoryTotalPages] = useState(0);
  const [loadingMarketHistory, setLoadingMarketHistory] = useState(false);

  // Modal pour l'historique des store points
  const [showStorePointsHistoryModal, setShowStorePointsHistoryModal] = useState(false);
  const [storePointsHistory, setStorePointsHistory] = useState<StorePointTransactionDTO[]>([]);
  const [storePointsHistoryPage, setStorePointsHistoryPage] = useState(0);
  const [, setStorePointsHistoryTotal] = useState(0);
  const [storePointsHistoryTotalPages, setStorePointsHistoryTotalPages] = useState(0);
  const [loadingStorePointsHistory, setLoadingStorePointsHistory] = useState(false);

  // Modal pour l'historique des transactions monétaires
  const [showMoneyHistoryModal, setShowMoneyHistoryModal] = useState(false);
  const [moneyHistory, setMoneyHistory] = useState<VaultTransactionDTO[]>([]);
  const [moneyHistoryPage, setMoneyHistoryPage] = useState(0);
  const [, setMoneyHistoryTotal] = useState(0);
  const [moneyHistoryTotalPages, setMoneyHistoryTotalPages] = useState(0);
  const [loadingMoneyHistory, setLoadingMoneyHistory] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [currentUuid, setCurrentUuid] = useState<string | null>(null);

  // Online status
  const [onlineStatus, setOnlineStatus] = useState<{ online: boolean; player: OnlinePlayerDTO | null } | null>(null);

  // Expanded sections
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());
  const [expandedFactionHomes, setExpandedFactionHomes] = useState(false);

  // Charger les donnees pour un UUID specifique (ou 'me' pour l'utilisateur connecte)
  const loadPlayerData = useCallback(async (uuid: string | null) => {
    setLoading({
      basicInfo: true,
      role: true,
      money: true,
      storePoints: true,
      nameHistory: true,
      homes: true,
      faction: true,
      jobs: true,
      sanctions: true,
      market: true,
    });
    setError(null);
    setExpandedServers(new Set());
    setExpandedFactionHomes(false);
    setShowMembersModal(false);
    setShowMarketHistoryModal(false);
    setOnlineStatus(null);

    // Charger toutes les donnees en parallele
    const isMe = uuid === null;

    // Basic Info
    const basicPromise = isMe
      ? apiService.getMyBasicInfo()
      : apiService.getBasicInfoByUuid(uuid!);

    basicPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, basicInfo: result.data! }));
        if (isMe && result.data.uuid) {
          setCurrentUuid(result.data.uuid);
        }
        // Check online status by player name
        if (result.data.name) {
          apiService.checkPlayerOnline(result.data.name).then((onlineResult) => {
            if (onlineResult.data) {
              setOnlineStatus(onlineResult.data);
            }
          });
        }
      } else if (result.error) {
        setError(result.error);
      }
      setLoading((prev) => ({ ...prev, basicInfo: false }));
    });

    // Role
    const rolePromise = isMe
      ? apiService.getMyRole()
      : apiService.getRoleByUuid(uuid!);

    rolePromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, role: result.data! }));
      }
      setLoading((prev) => ({ ...prev, role: false }));
    });

    // Money
    const moneyPromise = isMe
      ? apiService.getMyMoney()
      : apiService.getMoneyByUuid(uuid!);

    moneyPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, money: result.data! }));
      }
      setLoading((prev) => ({ ...prev, money: false }));
    });

    // Store Points
    const storePointsPromise = isMe
      ? apiService.getMyStorePoints()
      : apiService.getStorePointsByUuid(uuid!);

    storePointsPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, storePoints: result.data! }));
      }
      setLoading((prev) => ({ ...prev, storePoints: false }));
    });

    // Name History
    const nameHistoryPromise = isMe
      ? apiService.getMyNameHistory()
      : apiService.getNameHistoryByUuid(uuid!);

    nameHistoryPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, nameHistory: result.data! }));
      }
      setLoading((prev) => ({ ...prev, nameHistory: false }));
    });

    // Homes
    const homesPromise = isMe
      ? apiService.getMyHomes()
      : apiService.getHomesByUuid(uuid!);

    homesPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, personalHomes: result.data! }));
      }
      setLoading((prev) => ({ ...prev, homes: false }));
    });

    // Faction
    const factionPromise = isMe
      ? apiService.getMyFaction()
      : apiService.getFactionByUuid(uuid!);

    factionPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, faction: result.data! }));
      }
      setLoading((prev) => ({ ...prev, faction: false }));
    });

    // Jobs
    const jobsPromise = isMe
      ? apiService.getMyJobs()
      : apiService.getJobsByUuid(uuid!);

    jobsPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, jobs: result.data! }));
      }
      setLoading((prev) => ({ ...prev, jobs: false }));
    });

    // Sanctions
    const sanctionsPromise = isMe
      ? apiService.getMySanctions()
      : apiService.getSanctionsByUuid(uuid!);

    sanctionsPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, sanctions: result.data! }));
      }
      setLoading((prev) => ({ ...prev, sanctions: false }));
    });

    // Market Items
    const marketPromise = isMe
      ? apiService.getMyMarketItems()
      : apiService.getMarketItemsByUuid(uuid!);

    marketPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, marketItems: result.data!.items }));
      }
      setLoading((prev) => ({ ...prev, market: false }));
    });

    // Market History Count
    const marketHistoryCountPromise = isMe
      ? apiService.getMyMarketHistoryCount()
      : apiService.getMarketHistoryCountByUuid(uuid!);

    marketHistoryCountPromise.then((result) => {
      if (result.data) {
        setData((prev) => ({ ...prev, marketHistoryCount: result.data!.count }));
      }
    });
  }, []);

  // Load player data on mount - either from URL param or current user
  useEffect(() => {
    if (urlUuid) {
      // UUID dans l'URL - charger ce joueur specifique
      setCurrentUuid(urlUuid);
      loadPlayerData(urlUuid);
    } else {
      // Pas d'UUID - charger le profil de l'utilisateur connecte
      loadPlayerData(null);
    }
  }, [urlUuid, loadPlayerData]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchInput.trim()) return;

    setSearching(true);
    setError(null);

    // D'abord rechercher le joueur pour obtenir son UUID
    const searchResult = await apiService.searchPlayer(searchInput.trim());

    if (searchResult.error) {
      setError(searchResult.error);
      setSearching(false);
      return;
    }

    if (searchResult.data) {
      // On a trouve le joueur, charger ses donnees avec son UUID
      setCurrentUuid(searchResult.data.uuid);
      // Mettre a jour les infos de base tout de suite
      setData((prev) => ({ ...prev, basicInfo: searchResult.data! }));
      setLoading((prev) => ({ ...prev, basicInfo: false }));

      // Charger le reste des donnees
      const uuid = searchResult.data.uuid;

      // Charger en parallele
      Promise.all([
        apiService.getRoleByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, role: r.data! }));
          setLoading((prev) => ({ ...prev, role: false }));
        }),
        apiService.getMoneyByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, money: r.data! }));
          setLoading((prev) => ({ ...prev, money: false }));
        }),
        apiService.getStorePointsByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, storePoints: r.data! }));
          setLoading((prev) => ({ ...prev, storePoints: false }));
        }),
        apiService.getNameHistoryByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, nameHistory: r.data! }));
          setLoading((prev) => ({ ...prev, nameHistory: false }));
        }),
        apiService.getHomesByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, personalHomes: r.data! }));
          setLoading((prev) => ({ ...prev, homes: false }));
        }),
        apiService.getFactionByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, faction: r.data! }));
          setLoading((prev) => ({ ...prev, faction: false }));
        }),
        apiService.getJobsByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, jobs: r.data! }));
          setLoading((prev) => ({ ...prev, jobs: false }));
        }),
        apiService.getSanctionsByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, sanctions: r.data! }));
          setLoading((prev) => ({ ...prev, sanctions: false }));
        }),
        apiService.getMarketItemsByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, marketItems: r.data!.items }));
          setLoading((prev) => ({ ...prev, market: false }));
        }),
        apiService.getMarketHistoryCountByUuid(uuid).then((r) => {
          if (r.data) setData((prev) => ({ ...prev, marketHistoryCount: r.data!.count }));
        }),
      ]);

      setExpandedServers(new Set());
      setExpandedFactionHomes(false);
      setShowMarketHistoryModal(false);
    }

    setSearching(false);
  };

  const toggleServer = (server: string) => {
    setExpandedServers((prev) => {
      const next = new Set(prev);
      if (next.has(server)) {
        next.delete(server);
      } else {
        next.add(server);
      }
      return next;
    });
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Inconnu';
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatXp = (xp: number) => {
    if (xp >= 1000000) return `${(xp / 1000000).toFixed(1)}M`;
    if (xp >= 1000) return `${(xp / 1000).toFixed(1)}K`;
    return xp.toString();
  };

  const formatMoney = (money: number) => {
    return money.toLocaleString('fr-FR') + ' $';
  };

  const getServerDisplayName = (server: string) => {
    const match = server.match(/faction-(\d+)/);
    return match ? `Faction ${match[1]}` : server;
  };

  const getSanctionTypeClass = (type: number): string => {
    switch (type) {
      case 0:
        return styles.typeBan ?? '';
      case 1:
        return styles.typeMute ?? '';
      case 2:
        return styles.typeWarn ?? '';
      default:
        return '';
    }
  };

  const handleOpenMembersModal = async () => {
    if (!data.faction) return;

    setShowMembersModal(true);
    setLoadingMembers(true);
    setFactionMembers([]);

    const result = await apiService.getFactionMembers(data.faction.id);
    if (result.data) {
      setFactionMembers(result.data);
    }
    setLoadingMembers(false);
  };

  const handleMemberClick = (memberUuid: string) => {
    setShowMembersModal(false);
    navigate(`/players/${memberUuid}`);
  };

  const handleOpenClaimsModal = async () => {
    if (!data.faction) return;

    setShowClaimsModal(true);
    setLoadingClaims(true);
    setFactionClaims([]);

    const result = await apiService.getFactionClaims(data.faction.id);
    if (result.data) {
      setFactionClaims(result.data);
    }
    setLoadingClaims(false);
  };

  const handleOpenMarketHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowMarketHistoryModal(true);
    setLoadingMarketHistory(true);
    setMarketHistoryPage(page);

    const result = await apiService.getMarketHistoryByUuid(currentUuid, page, 10);
    if (result.data) {
      setMarketHistory(result.data.items);
      setMarketHistoryTotal(result.data.total);
      setMarketHistoryTotalPages(result.data.totalPages);
    }
    setLoadingMarketHistory(false);
  };

  const handleBuyerClick = (buyerUuid: string) => {
    setShowMarketHistoryModal(false);
    navigate(`/players/${buyerUuid}`);
  };

  const handleOpenStorePointsHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowStorePointsHistoryModal(true);
    setLoadingStorePointsHistory(true);
    setStorePointsHistoryPage(page);

    const result = await apiService.getStorePointTransactionsByUuid(currentUuid, page, 10);
    if (result.data) {
      setStorePointsHistory(result.data.items);
      setStorePointsHistoryTotal(result.data.total);
      setStorePointsHistoryTotalPages(result.data.totalPages);
    }
    setLoadingStorePointsHistory(false);
  };

  const handleOpenMoneyHistoryModal = async (page: number = 0) => {
    if (!currentUuid) return;

    setShowMoneyHistoryModal(true);
    setLoadingMoneyHistory(true);
    setMoneyHistoryPage(page);

    const result = await apiService.getMoneyTransactionsByUuid(currentUuid, page, 10);
    if (result.data) {
      setMoneyHistory(result.data.items);
      setMoneyHistoryTotal(result.data.total);
      setMoneyHistoryTotalPages(result.data.totalPages);
    }
    setLoadingMoneyHistory(false);
  };

  const formatStorePoints = (points: number) => {
    return points.toLocaleString('fr-FR') + ' PB';
  };

  const formatItemName = (name: string) => {
    if (!name) return 'Item inconnu';
    // Formater le nom: remplacer les underscores par des espaces et capitaliser
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatItemIdentifier = (identifier: string) => {
    if (!identifier) return '';
    // Retirer le namespace (minecraft:, paladium:, etc.)
    const parts = identifier.split(':');
    return parts.length > 1 ? parts[1] : identifier;
  };

  // Calculer si le joueur a une sanction active (ban ou mute)
  const getActiveSanction = () => {
    if (!data.sanctions || data.sanctions.length === 0) return null;

    const now = Date.now();
    for (const sanction of data.sanctions) {
      // Type 0 = ban, Type 1 = mute (ignorer les warns type 2)
      if (sanction.type > 1) continue;
      if (sanction.revoked) continue;

      // Si temporaire et expiree, ignorer
      if (sanction.temporary && sanction.expiryAt && sanction.expiryAt < now) continue;

      // Sanction active trouvee
      return sanction;
    }
    return null;
  };

  const activeSanction = !loading.sanctions ? getActiveSanction() : null;

  const isFullyLoading = loading.basicInfo && loading.role;

  if (isFullyLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Search bar */}
      <header className={styles.header}>
        <h1 className={styles.title}>Joueurs</h1>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Rechercher un joueur..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className={styles.searchButton} disabled={searching}>
            {searching ? <div className={styles.spinnerSmall} /> : 'Rechercher'}
          </button>
        </form>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {data.basicInfo && (
        <div className={styles.profileContainer}>
          {/* Active Sanction Banner */}
          {activeSanction && (
            <div className={`${styles.sanctionBanner} ${activeSanction.type === 0 ? styles.banBanner : styles.muteBanner}`}>
              <span className={styles.sanctionIcon}>{activeSanction.type === 0 ? '🚫' : '🔇'}</span>
              <div className={styles.sanctionBannerContent}>
                <span className={styles.sanctionBannerTitle}>
                  {activeSanction.type === 0 ? 'JOUEUR BANNI' : 'JOUEUR MUTE'}
                </span>
                <span className={styles.sanctionBannerReason}>
                  {activeSanction.description || 'Aucune raison specifiee'}
                </span>
                {activeSanction.temporary && activeSanction.expiryAt && (
                  <span className={styles.sanctionBannerExpiry}>
                    Expire le {formatDate(activeSanction.expiryAt)}
                  </span>
                )}
                {!activeSanction.temporary && (
                  <span className={styles.sanctionBannerExpiry}>Permanent</span>
                )}
              </div>
              <span className={styles.sanctionBannerStaff}>
                Par {activeSanction.staffName || 'Systeme'}
              </span>
            </div>
          )}

          {/* Player Header */}
          <section className={styles.playerHeader}>
            <div className={styles.skinContainer}>
              <img
                src="https://mc-heads.net/avatar/MHF_Steve/128"
                alt="Player skin"
                className={styles.skinImage}
              />
            </div>
            <div className={styles.playerInfo}>
              <div className={styles.playerNameRow}>
                <h2 className={styles.playerName}>{data.basicInfo.name || 'Inconnu'}</h2>
                {onlineStatus && (
                  onlineStatus.online ? (
                    <span className={styles.onlineBadge}>
                      <span className={styles.onlineDot} />
                      En ligne - {onlineStatus.player?.serverName || 'Serveur inconnu'}
                    </span>
                  ) : (
                    <span className={styles.offlineBadge}>
                      <span className={styles.offlineDot} />
                      Hors ligne
                    </span>
                  )
                )}
              </div>
              <div className={styles.playerMeta}>
                {loading.role ? (
                  <span className={styles.playerRole}>...</span>
                ) : (
                  <span className={styles.playerRole}>
                    {data.role?.roleDisplayName || data.role?.role || 'Joueur'}
                  </span>
                )}
                <span className={styles.playerUuid} title={data.basicInfo.uuid}>
                  {data.basicInfo.uuid}
                </span>
              </div>
              <div className={styles.playerDates}>
                <div>
                  <span className={styles.dateLabel}>Premiere connexion:</span>
                  <span className={styles.dateValue}>{formatDate(data.basicInfo.firstConnected)}</span>
                </div>
                <div>
                  <span className={styles.dateLabel}>Derniere connexion:</span>
                  <span className={styles.dateValue}>{formatDate(data.basicInfo.lastConnected)}</span>
                </div>
              </div>
            </div>
            {/* Money Card */}
            <div
              className={styles.moneyCard}
              onClick={() => handleOpenMoneyHistoryModal(0)}
              title="Cliquer pour voir l'historique"
            >
              <span className={styles.moneyIcon}>$</span>
              <div className={styles.moneyInfo}>
                <span className={styles.moneyLabel}>Solde</span>
                {loading.money ? (
                  <span className={styles.moneyValue}>...</span>
                ) : (
                  <span className={styles.moneyValue}>{formatMoney(data.money?.money || 0)}</span>
                )}
              </div>
            </div>
            {/* Points Boutique Card */}
            <div
              className={styles.storePointsCard}
              onClick={() => handleOpenStorePointsHistoryModal(0)}
              title="Cliquer pour voir l'historique"
            >
              <span className={styles.storePointsIcon}>💎</span>
              <div className={styles.storePointsInfo}>
                <span className={styles.storePointsLabel}>Points Boutique</span>
                {loading.storePoints ? (
                  <span className={styles.storePointsValue}>...</span>
                ) : (
                  <span className={styles.storePointsValue}>{formatStorePoints(data.storePoints?.totalStorePoints || 0)}</span>
                )}
              </div>
            </div>
          </section>

          <div className={styles.grid}>
            {/* Rank Manager */}
            {currentUuid && data.basicInfo && (
              <RankManager
                playerUuid={currentUuid}
                playerName={data.basicInfo.name || 'Inconnu'}
                onRankChange={() => {
                  // Reload role when rank changes
                  if (currentUuid) {
                    apiService.getRoleByUuid(currentUuid).then((r) => {
                      if (r.data) setData((prev) => ({ ...prev, role: r.data! }));
                    });
                  }
                }}
              />
            )}

            {/* Name History */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Historique des pseudos</h3>
              {loading.nameHistory ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.nameHistory.length === 0 ? (
                <p className={styles.emptyText}>Aucun historique</p>
              ) : (
                <ul className={styles.nameHistoryList}>
                  {data.nameHistory.map((entry, idx) => (
                    <li key={idx} className={styles.nameHistoryItem}>
                      <span className={styles.historyName}>{entry.name}</span>
                      <span className={styles.historyDate}>{formatDate(entry.firstSeen)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Personal Homes */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Homes personnels</h3>
              {loading.homes ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : Object.keys(data.personalHomes).length === 0 ? (
                <p className={styles.emptyText}>Aucun home</p>
              ) : (
                <div className={styles.homesList}>
                  {Object.entries(data.personalHomes).map(([server, homes]) => (
                    <div key={server} className={styles.serverGroup}>
                      <button className={styles.serverHeader} onClick={() => toggleServer(server)}>
                        <span className={styles.serverName}>
                          {getServerDisplayName(server)}
                          <span className={styles.homeCount}>{homes.length}</span>
                        </span>
                        <span className={styles.expandIcon}>{expandedServers.has(server) ? '−' : '+'}</span>
                      </button>
                      {expandedServers.has(server) && (
                        <ul className={styles.homeItems}>
                          {homes.map((home, idx) => (
                            <HomeItem
                              key={idx}
                              home={home}
                              server={server}
                              playerUuid={currentUuid}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Faction Info */}
            <section className={styles.card + ' ' + styles.factionCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>⚔</span>
                Faction
              </h3>
              {loading.faction ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : !data.faction ? (
                <p className={styles.emptyText}>Aucune faction</p>
              ) : (
                <div className={styles.factionInfo}>
                  <div className={styles.factionHeader}>
                    <div className={styles.factionTitleBlock}>
                      <span className={styles.factionName}>{data.faction.name}</span>
                      {data.faction.playerRoles && data.faction.playerRoles.length > 0 && (
                        <span className={styles.factionRole}>{data.faction.playerRoles.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  {data.faction.description && (
                    <div className={styles.factionDescriptionBlock}>
                      <span className={styles.infoLabel}>Description</span>
                      <p className={styles.factionDescription}>{data.faction.description}</p>
                    </div>
                  )}
                  <div className={styles.factionStats}>
                    <div className={`${styles.statItem} ${styles.statMembers}`} onClick={handleOpenMembersModal}>
                      <span className={styles.statValue}>{data.faction.membersCount}</span>
                      <span className={styles.statLabel}>Membres</span>
                      <span className={styles.statClickHint}>Cliquer pour voir</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statClaims}`} onClick={handleOpenClaimsModal}>
                      <span className={styles.statValue}>{data.faction.claimsCount}</span>
                      <span className={styles.statLabel}>Claims</span>
                      <span className={styles.statClickHint}>Cliquer pour voir</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statBank}`}>
                      <span className={styles.statValue}>{formatMoney(data.faction.bankBalance || 0)}</span>
                      <span className={styles.statLabel}>Banque</span>
                    </div>
                    <div className={`${styles.statItem} ${styles.statElo}`}>
                      <span className={styles.statValue}>{data.faction.elo || 0}</span>
                      <span className={styles.statLabel}>ELO</span>
                    </div>
                  </div>

                  {/* Faction Homes */}
                  {data.faction.homes && data.faction.homes.length > 0 && (
                    <div className={styles.factionHomes}>
                      <button
                        className={styles.serverHeader}
                        onClick={() => setExpandedFactionHomes(!expandedFactionHomes)}
                      >
                        <span className={styles.serverName}>
                          Homes de faction
                          <span className={styles.homeCount}>{data.faction.homes.length}</span>
                        </span>
                        <span className={styles.expandIcon}>{expandedFactionHomes ? '−' : '+'}</span>
                      </button>
                      {expandedFactionHomes && (
                        <ul className={styles.homeItems}>
                          {data.faction.homes.map((home, idx) => (
                            <FactionHomeItem key={idx} home={home} />
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Members Modal */}
            {showMembersModal && data.faction && (
              <div className={styles.modalOverlay} onClick={() => setShowMembersModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3>Membres de {data.faction.name}</h3>
                    <button className={styles.modalClose} onClick={() => setShowMembersModal(false)}>×</button>
                  </div>
                  <div className={styles.modalContent}>
                    {loadingMembers ? (
                      <div className={styles.modalLoading}>
                        <div className={styles.spinnerSmall} />
                        <span>Chargement des membres...</span>
                      </div>
                    ) : factionMembers.length === 0 ? (
                      <p className={styles.modalNote}>Aucun membre trouve ou vous n'avez pas la permission.</p>
                    ) : (
                      <ul className={styles.membersList}>
                        {factionMembers.map((member) => (
                          <FactionMemberItem
                            key={member.uuid}
                            member={member}
                            onClick={() => handleMemberClick(member.uuid)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Claims Modal */}
            {showClaimsModal && data.faction && (
              <div className={styles.modalOverlay} onClick={() => setShowClaimsModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3>Claims de {data.faction.name}</h3>
                    <button className={styles.modalClose} onClick={() => setShowClaimsModal(false)}>×</button>
                  </div>
                  <div className={styles.modalContent}>
                    {loadingClaims ? (
                      <div className={styles.modalLoading}>
                        <div className={styles.spinnerSmall} />
                        <span>Chargement des claims...</span>
                      </div>
                    ) : factionClaims.length === 0 ? (
                      <p className={styles.modalNote}>Aucun claim trouve ou vous n'avez pas la permission.</p>
                    ) : (
                      <div className={styles.claimsList}>
                        {/* Group claims by server */}
                        {Object.entries(
                          factionClaims.reduce((acc, claim) => {
                            if (!acc[claim.server]) acc[claim.server] = 0;
                            (acc[claim.server] as number) += claim.count;
                            return acc;
                          }, {} as Record<string, number>)
                        ).map(([server, totalClaims]) => (
                          <div key={server} className={styles.claimsServerGroup}>
                            <div className={styles.claimsServerHeader}>
                              <span className={styles.claimsServerName}>{getServerDisplayName(server)}</span>
                              <span className={styles.claimsServerTotal}>
                                {totalClaims} claims
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Jobs */}
            <section className={styles.card}>
              <h3 className={styles.cardTitle}>Jobs</h3>
              {loading.jobs ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : !data.jobs || !data.jobs.jobs || data.jobs.jobs.length === 0 ? (
                <p className={styles.emptyText}>Aucun job</p>
              ) : (
                <div className={styles.jobsList}>
                  {data.jobs.jobs.map((job) => (
                    <div key={job.id} className={styles.jobItem}>
                      <span className={styles.jobName}>{job.name}</span>
                      <span className={styles.jobXp}>{formatXp(job.xp)} XP</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Daily Quests */}
              {data.jobs && data.jobs.dailyQuests && data.jobs.dailyQuests.length > 0 && (
                <div className={styles.dailyQuests}>
                  <h4 className={styles.subTitle}>Quetes journalieres</h4>
                  {data.jobs.dailyQuests.map((quest, idx) => (
                    <div key={idx} className={`${styles.questItem} ${quest.completed ? styles.completed : ''}`}>
                      <div className={styles.questHeader}>
                        <span className={styles.questName}>{quest.name}</span>
                        <span className={styles.questReward}>+{quest.xpReward} XP</span>
                      </div>
                      <p className={styles.questDescription}>{quest.description}</p>
                      <div className={styles.questProgress}>
                        <div
                          className={styles.questProgressBar}
                          style={{ width: `${Math.min(100, (quest.currentProgress / quest.targetValue) * 100)}%` }}
                        />
                        <span className={styles.questProgressText}>
                          {quest.currentProgress} / {quest.targetValue}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Market */}
            <section className={styles.card + ' ' + styles.marketCard}>
              <h3 className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>🏪</span>
                Market
                {data.marketHistoryCount > 0 && (
                  <button
                    className={styles.marketHistoryButton}
                    onClick={() => handleOpenMarketHistoryModal(0)}
                    title="Voir l'historique des ventes"
                  >
                    {data.marketHistoryCount} ventes
                  </button>
                )}
              </h3>
              {loading.market ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.marketItems.length === 0 ? (
                <p className={styles.emptyText}>Aucun item en vente</p>
              ) : (
                <div className={styles.marketList}>
                  <table className={styles.marketTable}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Quantite</th>
                        <th>Prix</th>
                        <th>Expiration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.marketItems.map((item) => (
                        <tr key={item.id}>
                          <td className={styles.marketItemName}>
                            <span className={styles.itemName}>{formatItemName(item.name)}</span>
                            <span className={styles.itemId}>{formatItemIdentifier(item.identifier)}</span>
                          </td>
                          <td className={styles.marketItemCount}>{item.count}</td>
                          <td className={styles.marketItemPrice}>
                            {item.price > 0 && <span>{item.price.toLocaleString()} $</span>}
                            {item.pbPrice > 0 && <span className={styles.pbPrice}>{item.pbPrice} PB</span>}
                          </td>
                          <td className={styles.marketItemExpiry}>
                            {item.expirationDate ? formatDate(item.expirationDate) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Sanctions */}
            <section className={styles.card + ' ' + styles.fullWidth}>
              <h3 className={styles.cardTitle}>Sanctions</h3>
              {loading.sanctions ? (
                <div className={styles.loading}><div className={styles.spinnerSmall} /></div>
              ) : data.sanctions.length === 0 ? (
                <p className={styles.emptyText}>Aucune sanction</p>
              ) : (
                <div className={styles.sanctionsList}>
                  <table className={styles.sanctionsTable}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Staff</th>
                        <th>Raison</th>
                        <th>Duree</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sanctions.map((sanction) => (
                        <SanctionRow
                          key={sanction.id}
                          sanction={sanction}
                          getTypeClass={getSanctionTypeClass}
                          formatDate={formatDate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      )}

      {/* Modal Historique Market */}
      {showMarketHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowMarketHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des ventes</h3>
              <button className={styles.modalClose} onClick={() => setShowMarketHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingMarketHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : marketHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune vente dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.marketHistoryTable}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qte</th>
                        <th>Prix</th>
                        <th>Acheteur</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketHistory.map((item) => (
                        <tr key={item.id}>
                          <td className={styles.marketItemName}>
                            <span className={styles.itemName}>{formatItemName(item.name)}</span>
                          </td>
                          <td>{item.count}</td>
                          <td className={styles.marketItemPrice}>
                            {item.price > 0 && <span>{item.price.toLocaleString()} $</span>}
                            {item.pbPrice > 0 && <span className={styles.pbPrice}>{item.pbPrice} PB</span>}
                          </td>
                          <td>
                            {item.buyerUuid ? (
                              <button
                                className={styles.buyerLink}
                                onClick={() => handleBuyerClick(item.buyerUuid!)}
                              >
                                {item.buyerName || 'Inconnu'}
                              </button>
                            ) : (
                              item.buyerName || 'Inconnu'
                            )}
                          </td>
                          <td>{item.createdDate ? formatDate(item.createdDate) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {marketHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={marketHistoryPage === 0}
                        onClick={() => handleOpenMarketHistoryModal(marketHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {marketHistoryPage + 1} / {marketHistoryTotalPages}
                      </span>
                      <button
                        disabled={marketHistoryPage >= marketHistoryTotalPages - 1}
                        onClick={() => handleOpenMarketHistoryModal(marketHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Points Boutique */}
      {showStorePointsHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowStorePointsHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des transactions Points Boutique</h3>
              <button className={styles.modalClose} onClick={() => setShowStorePointsHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingStorePointsHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : storePointsHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune transaction dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.storePointsHistoryTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Raison</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storePointsHistory.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.timestamp)}</td>
                          <td className={transaction.amount >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                            {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toLocaleString()} PB
                          </td>
                          <td className={styles.transactionReason}>{transaction.reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {storePointsHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={storePointsHistoryPage === 0}
                        onClick={() => handleOpenStorePointsHistoryModal(storePointsHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {storePointsHistoryPage + 1} / {storePointsHistoryTotalPages}
                      </span>
                      <button
                        disabled={storePointsHistoryPage >= storePointsHistoryTotalPages - 1}
                        onClick={() => handleOpenStorePointsHistoryModal(storePointsHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Transactions Monétaires */}
      {showMoneyHistoryModal && (
        <div className={styles.modalOverlay} onClick={() => setShowMoneyHistoryModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Historique des transactions</h3>
              <button className={styles.modalClose} onClick={() => setShowMoneyHistoryModal(false)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {loadingMoneyHistory ? (
                <div className={styles.modalLoading}>
                  <div className={styles.spinnerSmall} />
                  <span>Chargement...</span>
                </div>
              ) : moneyHistory.length === 0 ? (
                <p className={styles.modalNote}>Aucune transaction dans l'historique.</p>
              ) : (
                <>
                  <table className={styles.moneyHistoryTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Expediteur</th>
                        <th>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moneyHistory.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.createdDate)}</td>
                          <td>{transaction.senderName || transaction.senderUuid}</td>
                          <td className={transaction.amount >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                            {transaction.amount >= 0 ? '+' : ''}{formatMoney(transaction.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {moneyHistoryTotalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        disabled={moneyHistoryPage === 0}
                        onClick={() => handleOpenMoneyHistoryModal(moneyHistoryPage - 1)}
                      >
                        &laquo;
                      </button>
                      <span className={styles.pageInfo}>
                        Page {moneyHistoryPage + 1} / {moneyHistoryTotalPages}
                      </span>
                      <button
                        disabled={moneyHistoryPage >= moneyHistoryTotalPages - 1}
                        onClick={() => handleOpenMoneyHistoryModal(moneyHistoryPage + 1)}
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HomeItem({
  home,
  server,
  playerUuid,
}: {
  home: HomeListItemDTO;
  server: string;
  playerUuid: string | null;
}) {
  const [coords, setCoords] = useState<HomeCoordsDTO | null>(null);
  const [loadingCoords, setLoadingCoords] = useState(false);
  const [coordsError, setCoordsError] = useState<string | null>(null);

  const handleClick = async () => {
    // Si on a deja les coords, toggle l'affichage
    if (coords) {
      setCoords(null);
      return;
    }

    // Sinon, charger les coords
    setLoadingCoords(true);
    setCoordsError(null);

    const result = playerUuid
      ? await apiService.getHomeCoordsbyUuid(playerUuid, server, home.name)
      : await apiService.getMyHomeCoords(server, home.name);

    if (result.error) {
      setCoordsError(result.error);
    } else if (result.data) {
      setCoords(result.data);
    }
    setLoadingCoords(false);
  };

  return (
    <li className={styles.homeItem} onClick={handleClick}>
      <span className={styles.homeName}>{home.name}</span>
      {loadingCoords && <span className={styles.homeCoords}>...</span>}
      {coordsError && <span className={styles.homeCoords} style={{ color: '#C53030' }}>Pas de permission</span>}
      {coords && (
        <span className={styles.homeCoords}>
          {Math.round(coords.x)}, {Math.round(coords.y)}, {Math.round(coords.z)} ({coords.world})
        </span>
      )}
    </li>
  );
}

function FactionHomeItem({ home }: { home: FactionHomeDTO }) {
  const [showCoords, setShowCoords] = useState(false);
  const serverName = home.server.replace('faction-', 'F');

  return (
    <li className={styles.homeItem} onClick={() => setShowCoords(!showCoords)}>
      <span className={styles.homeName}>[{serverName}] Home</span>
      {showCoords && (
        <span className={styles.homeCoords}>
          {home.x}, {home.y}, {home.z} ({home.world})
        </span>
      )}
    </li>
  );
}

function FactionMemberItem({
  member,
  onClick,
}: {
  member: FactionMemberDTO;
  onClick: () => void;
}) {
  const [onlineStatus, setOnlineStatus] = useState<{ online: boolean; player: OnlinePlayerDTO | null } | null>(null);

  useEffect(() => {
    // Check online status by member name
    if (member.name) {
      apiService.checkPlayerOnline(member.name).then((result) => {
        if (result.data) {
          setOnlineStatus(result.data);
        }
      });
    }
  }, [member.name]);

  return (
    <li className={styles.memberItem} onClick={onClick}>
      <div className={styles.memberLeft}>
        <img
          src="https://mc-heads.net/avatar/MHF_Steve/32"
          alt=""
          className={styles.memberAvatar}
        />
        <div className={styles.memberInfo}>
          <span className={styles.memberName}>{member.name || member.uuid}</span>
          {member.roles && member.roles.length > 0 && (
            <span className={styles.memberRoles}>{member.roles.join(', ')}</span>
          )}
        </div>
      </div>
      <div className={styles.memberRight}>
        {onlineStatus && (
          onlineStatus.online ? (
            <span className={styles.memberOnlineBadge}>
              <span className={styles.memberOnlineDot} />
              {onlineStatus.player?.serverName || 'En ligne'}
            </span>
          ) : (
            <span className={styles.memberOfflineBadge}>
              <span className={styles.memberOfflineDot} />
              Hors ligne
            </span>
          )
        )}
        <span className={styles.memberArrow}>→</span>
      </div>
    </li>
  );
}

function SanctionRow({
  sanction,
  getTypeClass,
  formatDate,
}: {
  sanction: SanctionDTO;
  getTypeClass: (type: number) => string;
  formatDate: (ts: number | null) => string;
}) {
  return (
    <tr className={sanction.revoked ? styles.revoked : ''}>
      <td>
        <span className={`${styles.typeBadge} ${getTypeClass(sanction.type)}`}>{sanction.typeName}</span>
      </td>
      <td>{sanction.staffName || 'Systeme'}</td>
      <td className={styles.reasonCell}>{sanction.description || '-'}</td>
      <td>{sanction.duration || '-'}</td>
      <td>{formatDate(sanction.createAt)}</td>
    </tr>
  );
}
